import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { Merchant } from '@/lib/merchants';
import { getMerchantStatus, formatRelativeTime, getCategoryInfo } from '@/lib/merchants';
import { VerifiedBadge } from './VerifiedBadge';
import { SponsoredBadge } from './SponsoredBadge';
import { StatusBadge } from './StatusBadge';
import { LocationLabel } from './LocationLabel';
import { MapPin, ExternalLink, Clock, Globe } from 'lucide-react';
import { sanitizeUrl } from '@/lib/sanitize';

interface MerchantCardProps {
  merchant: Merchant;
  isSelected: boolean;
  onClick: () => void;
}

export function MerchantCard({ merchant, isSelected, onClick }: MerchantCardProps) {
  const status = getMerchantStatus(merchant);

  return (
    <Card
      className={cn(
        'cursor-pointer transition-all duration-200 hover:shadow-md',
        isSelected
          ? 'ring-2 ring-primary shadow-md bg-primary/5'
          : 'hover:bg-accent/50',
        // Verified merchants get a green accent so they clearly stand out.
        merchant.verified && !isSelected && 'border-bch/40 bg-bch/[0.04] dark:bg-bch/[0.06] ring-1 ring-bch/20',
        merchant.sponsored && !isSelected && 'border-amber-300/50 bg-amber-50/30 dark:border-amber-700/30 dark:bg-amber-950/10',
      )}
      onClick={onClick}
    >
      <CardContent className="py-4 px-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Sponsored label */}
            {merchant.sponsored && (
              <div className="mb-1.5">
                <SponsoredBadge size="sm" />
              </div>
            )}

            {/* Name, verified badge */}
            <div className="flex items-center gap-1.5 mb-1.5">
              <h3 className="font-semibold text-sm truncate">{merchant.name}</h3>
              {merchant.verified && <VerifiedBadge size="sm" label />}
              {merchant.source === 'bchnostr' && !merchant.verified && (
                <Badge variant="outline" className="text-[10px] py-0 px-1.5 shrink-0 border-primary/30 text-primary">
                  BCHNostr
                </Badge>
              )}
            </div>

            {/* Summary */}
            {merchant.summary && (
              <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                {merchant.summary}
              </p>
            )}

            {/* Categories */}
            <div className="flex flex-wrap gap-1 mb-2">
              {merchant.categories.length > 0 ? (
                merchant.categories.slice(0, 3).map(cat => {
                  const info = getCategoryInfo(cat);
                  return (
                    <Badge key={cat} variant="secondary" className="text-xs py-0 px-1.5">
                      {info.emoji} {info.label}
                    </Badge>
                  );
                })
              ) : (
                <Badge variant="secondary" className="text-xs py-0 px-1.5">
                  Merchant
                </Badge>
              )}
            </div>

            {/* Meta info */}
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {merchant.location ? (
                <span className="flex items-center gap-1">
                  <MapPin className="size-3" />
                  <LocationLabel lat={merchant.location.lat} lng={merchant.location.lng} fallback={merchant.geohash} />
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <Globe className="size-3" />
                  <span>Online only</span>
                </span>
              )}
              {merchant.lastActive && (
                <span className="flex items-center gap-1">
                  <Clock className="size-3" />
                  {formatRelativeTime(merchant.lastActive)}
                </span>
              )}
            </div>
          </div>

          {/* Right side: Status badge + website */}
          <div className="flex flex-col items-end gap-2 shrink-0">
            <StatusBadge status={status} size="sm" />

            {merchant.website && (
              <a
                href={sanitizeUrl(merchant.website)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="size-3.5" />
              </a>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
